import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  api,
  CertificationExamAttemptStart,
  CertificationAnswer,
  CertificationExamResult,
  Problem,
} from "../lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Clock,
  XCircle,
  Award,
  AlertTriangle,
  ArrowRight,
  ChevronLeft,
  Trophy,
} from "lucide-react";

type ExamPhase = "loading" | "ready" | "in-progress" | "submitting" | "results";

function formatTimeLeft(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, "0")}`;
}

export default function CertificationExam() {
  const { examId } = useParams<{ examId: string }>();
  const navigate = useNavigate();

  const [phase, setPhase] = useState<ExamPhase>("loading");
  const [attempt, setAttempt] = useState<CertificationExamAttemptStart | null>(null);
  const [problems, setProblems] = useState<Problem[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState<Map<string, CertificationAnswer>>(new Map());
  const [currentAnswer, setCurrentAnswer] = useState("");
  const [timeLeft, setTimeLeft] = useState(0);
  const [result, setResult] = useState<CertificationExamResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const problemStartTime = useRef(Date.now());
  const examStartTime = useRef(Date.now());
  const inputRef = useRef<HTMLInputElement>(null);

  // Load exam data
  useEffect(() => {
    if (!examId) return;

    const loadExam = async () => {
      try {
        const attemptData = await api.startCertificationExam(examId);
        setAttempt(attemptData);
        setTimeLeft(attemptData.exam.time_limit_seconds);
        setPhase("ready");
      } catch (err: any) {
        setError(err.message || "Failed to start exam");
        setPhase("loading");
      }
    };

    loadExam();
  }, [examId]);

  // Timer countdown
  useEffect(() => {
    if (phase !== "in-progress" || timeLeft <= 0) return;

    const interval = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          handleSubmit();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [phase, timeLeft]);

  // Focus input on problem change
  useEffect(() => {
    if (phase === "in-progress" && inputRef.current) {
      inputRef.current.focus();
    }
  }, [currentIndex, phase]);

  const startExam = async () => {
    if (!attempt) return;

    // Load all problems
    const allProblems: Problem[] = [];
    const problemsByTechnique = new Map<string, typeof attempt.problems>();

    for (const p of attempt.problems) {
      const list = problemsByTechnique.get(p.technique_id) || [];
      list.push(p);
      problemsByTechnique.set(p.technique_id, list);
    }

    for (const [techniqueId, techProblems] of problemsByTechnique) {
      try {
        // Group by difficulty
        const byDiff = new Map<string, number>();
        for (const p of techProblems) {
          byDiff.set(p.difficulty, (byDiff.get(p.difficulty) || 0) + 1);
        }

        for (const [difficulty, count] of byDiff) {
          const generated = await api.generateProblems(
            techniqueId,
            count,
            difficulty,
          );
          allProblems.push(...generated);
        }
      } catch (err) {
        console.error(`Failed to generate problems for technique ${techniqueId}:`, err);
      }
    }

    // Map generated problems to attempt problem slots
    const mappedProblems: Problem[] = [];
    let genIdx = 0;
    for (const slot of attempt.problems) {
      if (genIdx < allProblems.length) {
        mappedProblems.push({
          ...allProblems[genIdx],
          id: slot.id, // Use the attempt's problem ID for tracking
        });
        genIdx++;
      }
    }

    setProblems(mappedProblems);
    examStartTime.current = Date.now();
    problemStartTime.current = Date.now();
    setPhase("in-progress");
  };

  const submitAnswer = useCallback(() => {
    if (!problems[currentIndex]) return;

    const problem = problems[currentIndex];
    const timeSpent = (Date.now() - problemStartTime.current) / 1000;

    const answer: CertificationAnswer = {
      problem_id: problem.id,
      answer: currentAnswer.trim(),
      correct_answer: problem.answer,
      time_seconds: Math.round(timeSpent * 10) / 10,
    };

    setAnswers((prev) => {
      const next = new Map(prev);
      next.set(problem.id, answer);
      return next;
    });

    setCurrentAnswer("");
    problemStartTime.current = Date.now();

    if (currentIndex < problems.length - 1) {
      setCurrentIndex((prev) => prev + 1);
    }
  }, [currentIndex, currentAnswer, problems]);

  const handleSubmit = useCallback(async () => {
    if (phase === "submitting" || !attempt) return;
    setPhase("submitting");

    // Submit current answer if filled
    if (currentAnswer.trim() && problems[currentIndex]) {
      const problem = problems[currentIndex];
      const timeSpent = (Date.now() - problemStartTime.current) / 1000;
      answers.set(problem.id, {
        problem_id: problem.id,
        answer: currentAnswer.trim(),
        correct_answer: problem.answer,
        time_seconds: Math.round(timeSpent * 10) / 10,
      });
    }

    const totalTime = Math.round((Date.now() - examStartTime.current) / 1000);

    try {
      const examResult = await api.submitCertificationExam(
        attempt.attempt_id,
        Array.from(answers.values()),
        totalTime,
      );
      setResult(examResult);
      setPhase("results");
    } catch (err: any) {
      setError(err.message || "Failed to submit exam");
      setPhase("in-progress");
    }
  }, [phase, attempt, currentAnswer, problems, currentIndex, answers]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && currentAnswer.trim()) {
      e.preventDefault();
      submitAnswer();
    }
  };

  // Error state
  if (error && phase === "loading") {
    return (
      <div className="container mx-auto px-4 py-8 max-w-2xl">
        <Card className="border-red-200 dark:border-red-800">
          <CardContent className="p-8 text-center">
            <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
              Cannot Start Exam
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">{error}</p>
            <Button onClick={() => navigate("/certifications")}>
              <ChevronLeft className="w-4 h-4 mr-2" />
              Back to Certifications
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Loading state
  if (phase === "loading") {
    return (
      <div className="container mx-auto px-4 py-8 max-w-2xl text-center">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mx-auto" />
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-2/3 mx-auto" />
        </div>
      </div>
    );
  }

  // Ready state - exam info & start button
  if (phase === "ready" && attempt) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-2xl">
        <Card>
          <CardContent className="p-8">
            <div className="text-center mb-8">
              <Award className="w-16 h-16 text-blue-600 mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                {attempt.exam.name}
              </h1>
              <p className="text-gray-600 dark:text-gray-400">
                {attempt.exam.description}
              </p>
            </div>

            <div className="grid grid-cols-3 gap-4 mb-8">
              <div className="text-center p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <p className="text-2xl font-bold text-blue-600">
                  {attempt.exam.problem_count}
                </p>
                <p className="text-xs text-gray-500">Problems</p>
              </div>
              <div className="text-center p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <p className="text-2xl font-bold text-blue-600">
                  {Math.floor(attempt.exam.time_limit_seconds / 60)}m
                </p>
                <p className="text-xs text-gray-500">Time Limit</p>
              </div>
              <div className="text-center p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <p className="text-2xl font-bold text-blue-600">
                  {attempt.exam.passing_accuracy}%
                </p>
                <p className="text-xs text-gray-500">Pass Mark</p>
              </div>
            </div>

            <div className="bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 rounded-lg p-4 mb-8">
              <h3 className="font-semibold text-amber-800 dark:text-amber-200 mb-2 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                Before you start
              </h3>
              <ul className="text-sm text-amber-700 dark:text-amber-300 space-y-1">
                <li>- The timer starts when you click "Start Exam"</li>
                <li>- You cannot pause once started</li>
                <li>- Problems are randomly generated for this attempt</li>
                <li>- You need {attempt.exam.passing_accuracy}% accuracy to pass</li>
              </ul>
            </div>

            <div className="flex gap-4 justify-center">
              <Button
                variant="outline"
                onClick={() => navigate("/certifications")}
              >
                <ChevronLeft className="w-4 h-4 mr-2" />
                Go Back
              </Button>
              <Button size="lg" onClick={startExam}>
                Start Exam
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // In-progress state
  if (phase === "in-progress" && problems.length > 0) {
    const problem = problems[currentIndex];
    const progress = ((currentIndex + 1) / problems.length) * 100;
    const answeredCount = answers.size;
    const isUrgent = timeLeft < 60;

    return (
      <div className="container mx-auto px-4 py-4 max-w-2xl">
        {/* Top bar: timer + progress */}
        <div className="flex items-center justify-between mb-4">
          <span className="text-sm text-gray-500">
            Problem {currentIndex + 1} of {problems.length}
          </span>
          <span
            className={`text-lg font-mono font-bold flex items-center gap-1 ${
              isUrgent ? "text-red-600 animate-pulse" : "text-gray-700 dark:text-gray-300"
            }`}
          >
            <Clock className="w-4 h-4" />
            {formatTimeLeft(timeLeft)}
          </span>
        </div>

        {/* Progress bar */}
        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mb-6">
          <div
            className="bg-blue-600 h-2 rounded-full transition-all"
            style={{ width: `${progress}%` }}
          />
        </div>

        {/* Problem */}
        <Card className="mb-6">
          <CardContent className="p-8">
            <div className="text-center mb-6">
              <span className="inline-block px-2 py-1 text-xs rounded bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 mb-4">
                {problem.difficulty}
              </span>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {problem.question}
              </p>
            </div>

            <div className="flex gap-3">
              <input
                ref={inputRef}
                type="text"
                value={currentAnswer}
                onChange={(e) => setCurrentAnswer(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Type your answer..."
                className="flex-1 px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg text-lg text-center bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                autoFocus
              />
              {currentIndex < problems.length - 1 ? (
                <Button
                  onClick={submitAnswer}
                  disabled={!currentAnswer.trim()}
                  size="lg"
                >
                  Next
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit}
                  disabled={!currentAnswer.trim()}
                  size="lg"
                  className="bg-green-600 hover:bg-green-700"
                >
                  Submit
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Quick navigation dots */}
        <div className="flex flex-wrap gap-1 justify-center mb-6">
          {problems.map((_, idx) => (
            <button
              key={idx}
              onClick={() => {
                // Save current answer before navigating
                if (currentAnswer.trim() && problems[currentIndex]) {
                  const p = problems[currentIndex];
                  const ts = (Date.now() - problemStartTime.current) / 1000;
                  setAnswers((prev) => {
                    const next = new Map(prev);
                    next.set(p.id, {
                      problem_id: p.id,
                      answer: currentAnswer.trim(),
                      correct_answer: p.answer,
                      time_seconds: Math.round(ts * 10) / 10,
                    });
                    return next;
                  });
                }
                setCurrentIndex(idx);
                const existing = answers.get(problems[idx].id);
                setCurrentAnswer(existing?.answer || "");
                problemStartTime.current = Date.now();
              }}
              className={`w-7 h-7 rounded text-xs font-medium transition-colors ${
                idx === currentIndex
                  ? "bg-blue-600 text-white"
                  : answers.has(problems[idx].id)
                  ? "bg-green-200 text-green-800 dark:bg-green-800 dark:text-green-200"
                  : "bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-400"
              }`}
            >
              {idx + 1}
            </button>
          ))}
        </div>

        {/* Submit button */}
        <div className="text-center">
          <p className="text-sm text-gray-500 mb-2">
            {answeredCount} of {problems.length} answered
          </p>
          <Button
            variant="outline"
            onClick={handleSubmit}
            className="border-red-300 text-red-600 hover:bg-red-50"
          >
            Submit Exam
          </Button>
        </div>
      </div>
    );
  }

  // Submitting state
  if (phase === "submitting") {
    return (
      <div className="container mx-auto px-4 py-8 max-w-2xl text-center">
        <div className="animate-pulse">
          <Award className="w-16 h-16 text-blue-600 mx-auto mb-4" />
          <p className="text-xl font-semibold text-gray-900 dark:text-white">
            Grading your exam...
          </p>
        </div>
      </div>
    );
  }

  // Results state
  if (phase === "results" && result) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-2xl">
        <Card
          className={
            result.passed
              ? "border-green-300 dark:border-green-700"
              : "border-red-300 dark:border-red-700"
          }
        >
          <CardContent className="p-8 text-center">
            {result.passed ? (
              <>
                <div className="w-20 h-20 rounded-full bg-green-100 dark:bg-green-900 flex items-center justify-center mx-auto mb-4">
                  <Trophy className="w-10 h-10 text-green-600" />
                </div>
                <h2 className="text-2xl font-bold text-green-700 dark:text-green-300 mb-2">
                  Congratulations! You Passed!
                </h2>
              </>
            ) : (
              <>
                <div className="w-20 h-20 rounded-full bg-red-100 dark:bg-red-900 flex items-center justify-center mx-auto mb-4">
                  <XCircle className="w-10 h-10 text-red-600" />
                </div>
                <h2 className="text-2xl font-bold text-red-700 dark:text-red-300 mb-2">
                  Not Quite There Yet
                </h2>
              </>
            )}

            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {result.passed
                ? "You've earned a certificate! Keep up the great work."
                : `You needed ${result.passing_accuracy}% to pass. Practice more and try again!`}
            </p>

            {/* Score details */}
            <div className="grid grid-cols-3 gap-4 mb-8">
              <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {result.accuracy.toFixed(1)}%
                </p>
                <p className="text-xs text-gray-500">Accuracy</p>
              </div>
              <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {result.problems_correct}/{result.problems_attempted}
                </p>
                <p className="text-xs text-gray-500">Correct</p>
              </div>
              <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {Math.floor(result.total_time_seconds / 60)}m{" "}
                  {result.total_time_seconds % 60}s
                </p>
                <p className="text-xs text-gray-500">Time Taken</p>
              </div>
            </div>

            {result.xp_earned > 0 && (
              <p className="text-sm text-blue-600 font-medium mb-4">
                +{result.xp_earned} XP earned!
              </p>
            )}

            {/* Certificate details */}
            {result.certificate && (
              <div className="bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 rounded-lg p-4 mb-6">
                <Award className="w-8 h-8 text-amber-600 mx-auto mb-2" />
                <p className="font-semibold text-amber-800 dark:text-amber-200">
                  {result.certificate.title}
                </p>
                <p className="text-sm text-amber-600 font-mono">
                  {result.certificate.certificate_number}
                </p>
              </div>
            )}

            <div className="flex gap-4 justify-center">
              {result.certificate && (
                <Button
                  onClick={() =>
                    navigate(`/certificate/${result.certificate!.id}`)
                  }
                >
                  <Award className="w-4 h-4 mr-2" />
                  View Certificate
                </Button>
              )}
              <Button
                variant="outline"
                onClick={() => navigate("/certifications")}
              >
                Back to Certifications
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return null;
}
